import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { Clock, CheckSquare, CalendarDays, ArrowRight, User } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/attendance/today").catch(() => ({ data: null })),
      api.get("/tasks"),
      api.get("/leaves"),
    ]).then(([att, tsk, lv]) => {
      setAttendance(att.data);
      setTasks(tsk.data);
      setLeaves(lv.data);
    }).finally(() => setLoading(false));
  }, []);

  const emp = user?.employee;
  const name = emp ? `${emp.first_name} ${emp.last_name}` : user?.name || "Employee";
  const pendingLeaves = leaves.filter((l) => l.status === "pending").length;
  const activeTasks = tasks.filter((t) => t.status !== "done").length;

  const quickActions = [
    { label: "Clock In / Out", desc: "Track your work hours", path: "/employee/attendance", icon: Clock, color: "bg-sky-50 text-[#38b6e8]" },
    { label: "Request Leave", desc: "Apply for time off", path: "/employee/leaves", icon: CalendarDays, color: "bg-amber-50 text-amber-600" },
    { label: "View My Tasks", desc: "See assigned tasks", path: "/employee/tasks", icon: CheckSquare, color: "bg-emerald-50 text-emerald-600" },
  ];

  return (
    <Layout>
      <div className="p-8 page-enter">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-[#1a2332] to-[#2c3e50] rounded-2xl p-7 mb-7 text-white relative overflow-hidden">
          <div className="absolute right-0 top-0 w-48 h-48 bg-[#38b6e8]/5 rounded-full translate-x-16 -translate-y-16" />
          <div className="relative z-10">
            <p className="text-slate-300 text-sm mb-1">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </p>
            <h1 className="text-2xl font-bold mb-1">Good {new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 17 ? "Afternoon" : "Evening"}, {emp?.first_name || name}!</h1>
            <p className="text-slate-400 text-sm">{emp?.job_role || "Welcome to your dashboard"}</p>
          </div>
        </div>

        {/* Today's attendance */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-7">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="today-attendance-card">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-sky-50 rounded-lg"><Clock size={16} className="text-[#38b6e8]" /></div>
              <p className="font-semibold text-slate-800 text-sm">Today's Attendance</p>
            </div>
            {loading ? <div className="h-16 animate-pulse bg-slate-100 rounded" /> : (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Clock In</span>
                  <span className={`font-semibold ${attendance?.clock_in ? "text-emerald-600" : "text-slate-400"}`}>
                    {fmt(attendance?.clock_in) || "Not yet"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Clock Out</span>
                  <span className={`font-semibold ${attendance?.clock_out ? "text-slate-700" : "text-slate-400"}`}>
                    {fmt(attendance?.clock_out) || (attendance?.clock_in ? "In progress" : "—")}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Active Hours</span>
                  <span className="font-bold text-[#1a2332]">
                    {attendance?.active_hours > 0 ? `${attendance.active_hours}h` : "—"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-emerald-50 rounded-lg"><CheckSquare size={16} className="text-emerald-600" /></div>
              <p className="font-semibold text-slate-800 text-sm">Active Tasks</p>
            </div>
            <p className="text-4xl font-bold text-[#1a2332]" data-testid="active-tasks-count">{activeTasks}</p>
            <p className="text-slate-500 text-xs mt-1">{tasks.filter((t) => t.status === "done").length} completed</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-amber-50 rounded-lg"><CalendarDays size={16} className="text-amber-600" /></div>
              <p className="font-semibold text-slate-800 text-sm">Leave Requests</p>
            </div>
            <p className="text-4xl font-bold text-[#1a2332]" data-testid="pending-leaves-count">{pendingLeaves}</p>
            <p className="text-slate-500 text-xs mt-1">pending approval</p>
          </div>
        </div>

        {/* Quick actions */}
        <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map(({ label, desc, path, icon: Icon, color }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 text-left hover:shadow-md hover:border-[#38b6e8]/40 transition-all group"
              data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-xl ${color}`}><Icon size={20} /></div>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-[#38b6e8] group-hover:translate-x-1 transition-all" />
              </div>
              <p className="font-semibold text-slate-800 text-sm">{label}</p>
              <p className="text-slate-500 text-xs mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
      </div>
    </Layout>
  );
}
